import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, BoxIcon } from 'lucide-react';
interface ProductCardProps {
  href: string;
  image: string;
  badgeText: string;
  badgeIcon: BoxIcon;
  category: string;
  title: string;
  subtitle: string;
  oldPrice?: string;
  newPrice: string;
  description: string;
  ctaText: string;
  recommended?: boolean;
  index?: number;
}
export function ProductCard({
  href,
  image,
  badgeText,
  badgeIcon: BadgeIcon,
  category,
  title,
  subtitle,
  oldPrice,
  newPrice,
  description,
  ctaText,
  recommended = false,
  index = 0
}: ProductCardProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-50px'
  }} transition={{
    duration: 0.5,
    delay: index % 3 * 0.1
  }} className="relative bg-white rounded-3xl overflow-hidden flex flex-col h-full shadow-sm hover:shadow-warm transition-shadow duration-300 ring-2 ring-brand-purple" data-id="element-1592">
      {/* Image — clickable, opens product page; gentle zoom on hover */}
      <a href={href} target="_top" aria-label={title} className="relative aspect-[16/10] overflow-hidden block group/image" data-id="element-1593">
        <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 ease-out group-hover/image:scale-105" data-id="element-1594" />

        {/* Recommended star badge top-right */}
        {recommended && <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-brand-gold text-white font-bold text-xs uppercase tracking-wider px-3 py-1.5 rounded-full shadow-warm" data-id="element-1595">
            <Star className="w-3.5 h-3.5 fill-current" data-id="element-1596" />
            Recomandat
          </div>}

        {/* Type badge bottom-left — uniform gold */}
        <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-brand-gold/95 text-white text-sm font-medium px-3 py-1.5 rounded-full backdrop-blur-sm" data-id="element-1597">
          <BadgeIcon className="w-4 h-4" data-id="element-1598" />
          {badgeText}
        </div>
      </a>

      {/* Content */}
      <div className="p-6 md:p-7 flex flex-col flex-grow" data-id="element-1599">
        <span className="text-xs font-bold uppercase tracking-[0.15em] text-brand-purple mb-3" data-id="element-1600">
          {category}
        </span>
        <h3 className="font-heading text-2xl md:text-[26px] font-bold text-brand-purple mb-1 leading-tight text-balance" data-id="element-1601">
          {title}
        </h3>
        <p className="font-subtitle italic text-base text-brand-purple/80 mb-4 leading-snug" data-id="element-1602">
          {subtitle}
        </p>

        <div className="flex items-baseline gap-2 mb-4" data-id="element-1603">
          {oldPrice && <span className="text-brand-text/40 line-through text-base decoration-1" data-id="element-1604">
              {oldPrice}
            </span>}
          <span className="font-heading font-bold text-2xl text-brand-text" data-id="element-1605">
            {newPrice}
          </span>
        </div>

        <p className="text-brand-textSec leading-relaxed text-[15px] mb-6 flex-grow" data-id="element-1606">
          {description}
        </p>

        <a href={href} target="_top" className="group bg-brand-gold text-white hover:bg-brand-purple font-heading font-semibold text-base py-3.5 rounded-full transition-all duration-300 flex items-center justify-center gap-2 hover:-translate-y-0.5 shadow-sm hover:shadow-warm" data-id="element-1607">
          {ctaText}
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-1608" />
        </a>
      </div>
    </motion.div>;
}